/**
 * Canonical money display (Wave 3).
 * UAE is a profile — not the platform implementation.
 */

export type MoneyDisplayMode = 'symbol_prefix' | 'code_prefix' | 'plain';

export type FormatMoneyOptions = {
  currency?: string;
  locale?: string;
  /** When omitted, use locale default / 'en-US' for profiles that pin locale. */
  display?: MoneyDisplayMode;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Symbol used for non-AED `symbol_prefix` (legacy CurrencyContext). */
  symbol?: string;
  /**
   * Spacing between code/symbol and amount.
   * UAE UI/print: NBSP; brief/email legacy: regular space.
   */
  separator?: string;
  /** Empty string for null/undefined (default). Never silently coerce invalid → 0 unless caller asks. */
  nullDisplay?: string;
};

export const CURRENCY_NBSP = '\u00A0';

/** Today’s UI/print AED plain-text convention. */
export const UAE_MONEY_PROFILE: FormatMoneyOptions = {
  currency: 'AED',
  locale: 'en-US',
  display: 'code_prefix',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  separator: CURRENCY_NBSP,
};

/** AI approval brief legacy: `AED 1,234.56` with en-AE + regular space. */
export const LEGACY_BRIEF_MONEY_PROFILE: FormatMoneyOptions = {
  currency: 'AED',
  locale: 'en-AE',
  display: 'code_prefix',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  separator: ' ',
};

/** Email Handlebars legacy: `AED 1,234.56` en-US + regular space. */
export const LEGACY_EMAIL_MONEY_PROFILE: FormatMoneyOptions = {
  currency: 'AED',
  locale: 'en-US',
  display: 'code_prefix',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  separator: ' ',
};

/**
 * Quotation / many print tables: amount only, browser/default locale, 2dp (no currency code).
 * Preserves prior `toLocaleString(undefined, { min/max 2 })`.
 */
export const PRINT_PLAIN_AMOUNT_PROFILE: FormatMoneyOptions = {
  display: 'plain',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function coerceAmount(amount: unknown): number | null {
  if (amount === null || amount === undefined) return null;
  if (typeof amount === 'string' && amount.trim() === '') return null;
  const n = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(n) ? n : null;
}

export function formatMoney(amount: unknown, options: FormatMoneyOptions = {}): string {
  const opts = { ...options };
  const nullDisplay = opts.nullDisplay ?? '';
  const n = coerceAmount(amount);
  if (n === null) return nullDisplay;

  const min = opts.minimumFractionDigits ?? 2;
  const max = opts.maximumFractionDigits ?? 2;
  const locale = opts.locale; // undefined = runtime default (browser/Node)
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(n);
  } catch {
    formatted = n.toFixed(Math.min(Math.max(max, 0), 20));
  }

  const display = opts.display ?? 'code_prefix';
  if (display === 'plain') return formatted;

  const code = (opts.currency || 'AED').toUpperCase();
  const sep = opts.separator ?? ' ';

  if (display === 'code_prefix') {
    return `${code}${sep}${formatted}`;
  }

  // symbol_prefix
  const symbol = opts.symbol || code;
  if (!symbol) return formatted;
  // AED plain-text: never emit U+20C3 (missing glyph) — use ISO code
  if (code === 'AED') {
    return `AED${opts.separator ?? CURRENCY_NBSP}${formatted}`;
  }
  return `${symbol}${formatted}`;
}

export function formatMoneyWithProfile(
  amount: unknown,
  profile: FormatMoneyOptions,
  overrides?: FormatMoneyOptions
): string {
  return formatMoney(amount, { ...profile, ...overrides });
}
